/*-----------------------------------------------------------------------------*
 *  TOGGLER.JS
 *-----------------------------------------------------------------------------*
 *
 *  Toggle attributes and classes using only HTML [data] attributes.
 *
 *  @author  Luca Rosaldi
 *  @version 1.0.0
 *
 *
 *  Data-attributes:
 *
 *  [data-toggle="{selector}"] (default: current element)
 *  CSS selector for element(s) to toggle. Also accepts the keyword "parent",
 *  which targets the parent of the current element.
 *
 *  [data-toggle-attr="{name}"] (default: 'class')
 *  Attribute to toggle. If not specified, toggle classes.
 *
 *  [data-toggle-value="{value}"] (default: 'is-active')
 *  [data-toggle-class="{value}"]
 *  Attribute value or CSS class(es) to toggle.
 *
 *  [data-toggle-event="{event}"] (default: 'click')
 *  The (javascript) event type to respond to.
 *
 *  [data-toggle-text="Some text"]
 *  Replace text on trigger when it is toggled.
 *
 *  [data-toggle-once]
 *  Only fire the first time the element is toggled.
 *
 *  [data-toggle-prevent]
 *  Prevent the default event.
 *
 */

( function( global ) {
  "use strict";

  /**
   * Constructor.
   */
  function Toggler() {
    this.selector = '[data-toggle]';

    this._addEvent = this._addEvent.bind( this );
    this._removeEvent = this._removeEvent.bind( this );
    this._handleEvent = this._handleEvent.bind( this );

    this.init();
  }

  /**
   * Initialize module.
   *
   * @access public
   *
   * @return {Toggler}
   */
  Toggler.prototype.init = function() {
    [].forEach.call( document.querySelectorAll( this.selector ), ( element ) => this._addEvent( element ) );
    return this;
  };

  /**
   * Destroy module.
   *
   * @access public
   *
   * @return {Toggler}
   */
  Toggler.prototype.destroy = function() {
    [].forEach.call( document.querySelectorAll( this.selector ), ( element ) => this._removeEvent( element ) );
    return this;
  };

  /**
   * Refresh module.
   *
   * @access public
   *
   * @return {Toggler}
   */
  Toggler.prototype.refresh = function() {
    return this.destroy().init();
  };

  /**
   * Toggle classes for a specific element.
   *
   * @access public
   *
   * @param  {Node}    trigger
   * @return {Toggler}
   */
  Toggler.prototype.toggle = function( trigger ) {
    const targets = getTargets( trigger );
    const attr = trigger.getAttribute( 'data-toggle-attr' );
    const value = trigger.getAttribute( 'data-toggle-value' ) || trigger.getAttribute( 'data-toggle-class' ) || 'is-active';
    const textReplace = trigger.getAttribute( 'data-toggle-text' );

    // toggle classes for all targets
    targets.forEach( ( target ) => {

      // remove attribute
      if ( attr && target.getAttribute( attr ) ) {
        target.removeAttribute( attr );
      }
      // set attribute
      else if ( attr ) {
        target.setAttribute( attr, value );
      }
      // toggle classes
      else {
        toggleClasses( target, value );
      }

    });

    // toggle default active class for the trigger
    if ( targets.indexOf( trigger ) < 0 ) {
      trigger.classList.toggle( 'is-active' );
    }

    // replace text inside the trigger
    if ( textReplace ) {
      trigger.setAttribute( 'data-toggle-text', trigger.innerText );
      trigger.innerText = textReplace;
    }

    return this;
  }

  /**
   * Add event listener for trigger element.
   *
   * @access private
   *
   * @param  {Node}    element
   * @return {Toggler}
   */
  Toggler.prototype._addEvent = function( element ) {
    const eventType = element.getAttribute( 'data-toggle-event' ) || 'click';
    const handler = this._handleEvent;

    element.addEventListener( eventType, handler );

    // add mouseout trigger for mouseover events
    if ( eventType === 'mouseover' ) {
      element.addEventListener( 'mouseout', handler );
    }

    return this;
  };

  /**
   * Remove event listener for trigger element.
   *
   * @access private
   *
   * @param  {Node}    element
   * @return {Toggler}
   */
  Toggler.prototype._removeEvent = function( element ) {
    const eventType = element.getAttribute( 'data-toggle-event' ) || 'click';
    const handler = this._handleEvent;

    element.removeEventListener( eventType, handler );

    if ( eventType === 'mouseover' ) {
      element.removeEventListener( 'mouseout', handler );
    }

    return this;
  };

  /**
   * Handle events for trigger element.
   *
   * @access private
   *
   * @param  {Event}   event
   * @return {Toggler}
   */
  Toggler.prototype._handleEvent = function( event ) {
    if ( event.target.hasAttribute( 'data-toggle-prevent' ) ) {
      event.preventDefault();
    }
    if ( event.target.hasAttribute( 'data-toggle-once' ) ) {
      this._removeEvent( event.target );
    }

    return this.toggle( event.target );
  };

  /**
   * Get targets for a specific trigger.
   *
   * @param  {Node}        trigger
   * @return {Array<Node>}
   */
  function getTargets( trigger ) {
    const selector = trigger.getAttribute( 'data-toggle' );
    if ( ! selector ) {
      return [ trigger ];
    }
    if ( selector === 'parent' ) {
      return [ trigger.parentNode ];
    }
    return [].slice.call( document.querySelectorAll( selector ) );
  }

  /**
   * Helper for toggling multiple classes at once, as IE 10 and 11
   * do not support that feature in classList.
   *
   * @param  {Node}         element
   * @param  {Array|String} classes
   * @return {Node}         element
   */
  function toggleClasses( element, classes = [] ) {

    // turn string into array of class names
    if ( typeof classes === 'string' ) {
      classes = classes.replace( ',', ' ' );
      classes = classes.split( /\s+/ );
    }

    classes.forEach( function( className ) {
      element.classList.toggle( className );
    });

    return element;
  }

  global.Toggler = new Toggler();

})( window );
